"use client";

import { MessageChannel } from "@prisma/client";
import { ReactNode } from "react";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import IconPlus from "@/app/(components)/generics/Icons/IconPlus";
import { SelectInput } from "@/app/(components)/generics/inputs/default/SelectInput/SelectInput";
import CircleLoader from "@/app/(components)/generics/loaders/CircleLoader/CircleLoader";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import ObjectiveSelector from "@/app/(components)/specifics/tools/MessagesContent/components/Sequences/components/ObjectiveSelector/ObjectiveSelector";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { PageParams } from "@/app/types/common/DefaultProps";
import {
  MessageSequenceStepData,
  MessageSequenceStepVariantData,
} from "@/app/types/messages-content/MessageSequenceData";
import { SequenceObjectiveData } from "@/app/types/messages-content/SequenceObjectiveData";
import { UDate } from "@/app/utils/UDate";
import { Limits } from "@/app/vars/Limits";
import {
  CHANNEL_ICONS,
  CHANNEL_LABEL_KEYS,
  PLACEHOLDER_SEQUENCES,
  splitAvatarUpdatedMessage,
} from "./MessageSequence.logic";
import styles from "./MessageSequence.module.css";
import SequenceStepRow from "./SequenceStepRow";

type Props = {
  params: PageParams;
  channel: MessageChannel;
  steps: MessageSequenceStepData[];
  isPlaceholder?: boolean;
  isLoading?: boolean;
  isGenerating?: boolean;
  regeneratingVariantIds?: number[];
  hasStepsHint?: boolean;
  needUpdate?: boolean;
  avatarUpdatedAt?: string | null;
  onChangeChannel: (channel: MessageChannel) => void;
  objectives?: SequenceObjectiveData[];
  objectiveId?: number;
  onChangeObjective: (objectiveId: number) => void;
  onEditObjective?: (objective: SequenceObjectiveData) => void;
  onDeleteObjective?: (objective: SequenceObjectiveData) => void;
  onCreateObjective?: () => void;
  onAddStep?: () => void;
  onEditStep?: (step: MessageSequenceStepData) => void;
  onDeleteStep?: (step: MessageSequenceStepData) => void;
  onAddVariant?: (step: MessageSequenceStepData) => void;
  onEditVariant?: (step: MessageSequenceStepData, variant: MessageSequenceStepVariantData) => void;
  onRegenerateVariant?: (
    step: MessageSequenceStepData,
    variant: MessageSequenceStepVariantData,
  ) => void;
  onDeleteVariant?: (variant: MessageSequenceStepVariantData) => void;
  onGenerate?: () => void;
};

export default function MessageSequence(props: Props) {
  const T = useT();
  const {
    params,
    channel,
    steps,
    isPlaceholder = false,
    isLoading = false,
    isGenerating = false,
    regeneratingVariantIds,
    hasStepsHint = false,
    needUpdate = false,
    avatarUpdatedAt,
    onChangeChannel,
    objectives = [],
    objectiveId,
    onChangeObjective,
    onEditObjective,
    onDeleteObjective,
    onCreateObjective,
    onAddStep,
    onEditStep,
    onDeleteStep,
    onAddVariant,
    onEditVariant,
    onRegenerateVariant,
    onDeleteVariant,
    onGenerate,
  } = props;

  const channelOptions = Object.values(MessageChannel).map((value) => {
    const Icon = CHANNEL_ICONS[value];
    return [
      value,
      <span key={value} className={styles.channelOption}>
        <Icon size={16} />
        {T(CHANNEL_LABEL_KEYS[value])}
      </span>,
    ] as [string, ReactNode];
  });

  const visibleSteps = isPlaceholder ? PLACEHOLDER_SEQUENCES[channel] : steps;
  const hasSteps = visibleSteps.length > 0;
  const showRegenerate = hasSteps && !isPlaceholder;
  const atLimit = steps.length >= Limits.MAX_SEQUENCE_STEPS;

  const showUpdateBanner =
    !isPlaceholder && hasStepsHint && needUpdate && !!onGenerate && !isGenerating;

  const [updatedBefore, updatedAfter] = splitAvatarUpdatedMessage(
    String(T(TKey.MESSAGES_CONTENT_AVATAR_UPDATED)),
  );

  return (
    <div className={styles.card}>
      {showUpdateBanner && (
        <div className={styles.updateBanner}>
          <p className={styles.updateText}>
            {updatedBefore}
            {avatarUpdatedAt && (
              <span className={styles.updateAgo}>
                {" "}
                ({UDate.timeAgo(new Date(avatarUpdatedAt), params.lang)})
              </span>
            )}
            {updatedAfter}
          </p>
          <Button
            text={T(TKey.MESSAGES_CONTENT_REGENERATE_CTA) as string}
            type={ButtonType.PRIMARY}
            isLink={false}
            onClick={onGenerate}
            min={true}
          />
        </div>
      )}

      <div className={styles.cardTop}>
        <ObjectiveSelector
          objectives={objectives}
          objectiveId={objectiveId}
          onChange={onChangeObjective}
          onEdit={onEditObjective}
          onDelete={onDeleteObjective}
          onCreate={onCreateObjective}
        />

        <div className={styles.channel}>
          <SelectInput
            label={isPlaceholder ? "" : (T(TKey.MESSAGES_CONTENT_CHANNEL_LABEL) as string)}
            data={{ channel }}
            dataKey="channel"
            options={channelOptions}
            onChange={(d) => onChangeChannel(d.channel as MessageChannel)}
          />
        </div>
      </div>

      {isGenerating ? (
        <div className={styles.loading}>
          <CircleLoader size={20} color="var(--color-green-3)" />
          <span className={styles.loadingText}>{T(TKey.MESSAGES_CONTENT_GENERATING)}</span>
        </div>
      ) : isLoading ? (
        <div className={styles.loading}>
          <PointsLoader color="var(--color-green-3)" delay={Limits.PROSPECT_SEARCH_LOADER_DELAY} />
        </div>
      ) : (
        <>
          {visibleSteps.length > 0 && (
            <div className={styles.steps}>
              {visibleSteps.map((step, index) => (
                <SequenceStepRow
                  key={step.id}
                  params={params}
                  step={step}
                  channel={channel}
                  regeneratingVariantIds={regeneratingVariantIds}
                  isLast={index === visibleSteps.length - 1}
                  isPlaceholder={isPlaceholder}
                  canAddVariant={
                    !isPlaceholder && step.variants.length < Limits.MAX_SEQUENCE_STEP_VARIANTS
                  }
                  onEdit={isPlaceholder || !onEditStep ? undefined : () => onEditStep(step)}
                  onDelete={isPlaceholder || !onDeleteStep ? undefined : () => onDeleteStep(step)}
                  onAddVariant={
                    isPlaceholder || !onAddVariant ? undefined : () => onAddVariant(step)
                  }
                  onEditVariant={
                    isPlaceholder || !onEditVariant
                      ? undefined
                      : (variant) => onEditVariant(step, variant)
                  }
                  onRegenerateVariant={
                    isPlaceholder || !onRegenerateVariant
                      ? undefined
                      : (variant) => onRegenerateVariant(step, variant)
                  }
                  onDeleteVariant={isPlaceholder ? undefined : onDeleteVariant}
                />
              ))}
            </div>
          )}

          {!isPlaceholder && !hasSteps && (
            <p className={styles.emptyText}>{T(TKey.MESSAGES_CONTENT_SEQUENCE_EMPTY)}</p>
          )}

          {!isPlaceholder && !atLimit && onAddStep && (
            <div className={styles.addRow}>
              <button type="button" className={styles.addBtn} onClick={onAddStep}>
                <IconPlus />
                <span>{T(TKey.MESSAGES_CONTENT_ADD_STEP)}</span>
              </button>
            </div>
          )}
        </>
      )}

      {!isLoading && !isGenerating && onGenerate && (
        <div className={styles.actions}>
          <Button
            text={
              T(
                showRegenerate
                  ? TKey.MESSAGES_CONTENT_REGENERATE_CTA
                  : TKey.MESSAGES_CONTENT_GENERATE_CTA,
              ) as string
            }
            type={showRegenerate ? ButtonType.OUTLINE : ButtonType.PRIMARY}
            isLink={false}
            onClick={onGenerate}
            min={true}
          />
        </div>
      )}
    </div>
  );
}
